/**
 * OKS-35 prop-contract types for per-collab content submissions.
 *
 * Backend is authoritative for every shape here — wire format is
 * snake_case (matches Inertia page-prop convention; QA contract gate
 * catches drift).
 */

/** State chip color/text driver. */
export type SubmissionState = "pending" | "approved" | "flagged"

/**
 * OKS-399 — Advisory auto-check status. `indeterminate` renders the
 * neutral "—" row (NOT a ✗) when inputs are missing (FR-6).
 */
export type AutoCheckStatus = "pass" | "fail" | "indeterminate"

/**
 * OKS-399 — A single auto-check result. `detail_key` selects the i18n
 * string under `submissions.brand.autochecks.<check>.<detail_key>`;
 * `detail_params` carries its interpolations (e.g. `{ date }` as an
 * iso8601 string the FE formats, `{ handle }` for the missing mention).
 * Computed server-side by `Submissions::AutoChecks` — never re-derived
 * on the FE.
 */
export interface AutoCheck {
  status: AutoCheckStatus
  detail_key: string
  detail_params: Record<string, string>
}

/** OKS-399 — The two computable checks shown in the approve dialog. */
export interface AutoChecks {
  posted_on_time: AutoCheck
  brand_mention: AutoCheck
}

/**
 * A single submission row. Returned by both creator + brand surfaces.
 * `flag_note` is creator-visible (UQ-2 lock) — render in a
 * `<Collapsible>` with `<blockquote>` semantics.
 *
 * `auto_checks` is present only on the brand-side sheet payload
 * (`submissions_for_open_collab`); optional so the creator-side shape
 * (which omits it) still satisfies the type.
 */
export interface Submission {
  id: number
  collab_id: number
  tiktok_video_id: string
  tiktok_video_url: string
  tiktok_video_thumbnail_url: string | null
  caption_excerpt: string | null
  posted_at: string | null
  state: SubmissionState
  submitted_at: string
  approved_at: string | null
  flagged_at: string | null
  flag_note: string | null
  auto_checks?: AutoChecks
  /**
   * OKS-964 — POST target for "make this the mission's Coup de cœur", or null.
   *
   * Non-null IFF the row is `approved` AND the mission has no pick yet AND the
   * actor passes `KudoPolicy#create?`. The OKS-707 rule applies: the affordance
   * renders iff this is non-null — there is no disabled-with-tooltip path.
   *
   * OPTIONAL, like `auto_checks`, and for the same reason: `BrandHomeController`
   * and `Brand::ContentGalleriesController` mirror this field set and neither
   * emits it. Making it required would red their consumers for no behavioural
   * reason.
   */
  coup_de_coeur_url?: string | null
}

/**
 * OKS-964 — the mission's ONE Coup de cœur, or null when unpicked.
 *
 * MISSION-level, not collab-level: the cap is per mission while the Sheet is
 * scoped to a single collab, so a Sheet opened on any collab still knows the
 * mission's pick. That is what makes the silent rows (UX §2.3) legible via the
 * cap line (§2.4) rather than mysterious (PRD OQ-4).
 */
export interface MissionCoupDeCoeur {
  submission_id: number
  collab_id: number | null
  creator_handle: string | null
  marked_at: string
}

/**
 * Submission counts for a brand panel collab row. Drives the count chip
 * + "View submissions (N/3)" overflow item. All four keys are always
 * present — `0` rather than missing.
 */
export interface SubmissionCounts {
  pending: number
  approved: number
  flagged: number
  total: number
}

/**
 * One eligible-media tile in the picker grid. `ineligible_reason` is
 * non-null when the tile must be dimmed + non-clickable; the FE shows
 * a tooltip per `picker.already_submitted_*`.
 */
export interface EligibleMediaTile {
  id: string
  url: string
  thumbnail_url: string | null
  caption_excerpt: string | null
  posted_at: string | null
  view_count: number | null
  /**
   * OKS-481 (§09) — selected-tile metrics row. Already ride the TikTok
   * payload; `null` when the metric is absent → the FE hides that metric.
   */
  like_count: number | null
  comment_count: number | null
  ineligible_reason:
    | null
    | "already_submitted_here"
    | "already_submitted_other_collab"
  ineligible_other_mission_title: string | null
}

/**
 * OKS-481 (§09) — Brand identity for the picker BrandStrip + the
 * "Submit to {brand}" CTA. Always the CURRENT collab's own brand
 * (isolation P0) — never another brand's name surfaces in the picker.
 */
export interface PickerBrand {
  name: string
  avatar_url: string | null
}

/**
 * OKS-391 — Profile-strip payload (creator's own TikTok identity +
 * latest captured metrics). `null` on the picker prop when identity is
 * unavailable (`missing_social_account` / `tiktok_auth_revoked`) → the
 * strip is hidden. Stat counts are `null` when no metrics snapshot has
 * been captured yet → the FE renders "—" (PRD OQ-3).
 */
export interface TikTokProfile {
  handle: string
  avatar_url: string | null
  display_name: string | null
  following_count: number | null
  follower_count: number | null
  likes_count: number | null
}

/** Picker partial-reload payload. `error_code` mirrors the service. */
export interface PickerProps {
  collab_id: number
  filter_active: boolean
  error_code:
    | null
    | "tiktok_unreachable"
    | "tiktok_auth_revoked"
    | "tiktok_rate_limited"
    | "missing_social_account"
  /** OKS-481 (§09) — current collab's brand, for BrandStrip + CTA. */
  brand: PickerBrand | null
  profile: TikTokProfile | null
  tiles: EligibleMediaTile[]
}

/** Hard cap. Mirrors `Submission::MAX_PER_COLLAB` (Ruby side). */
export const SUBMISSION_CAP = 3
